import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import CTA from '../components/Cta';
import FAQ from '../components/Faq';
import toast from 'react-hot-toast';
import { useSEO } from '../hooks/useSEO';

const CustomCards = () => {
  useSEO({
    title: "Custom NFC Business Cards | AM CARDMATE",
    description: "Design unique custom NFC business cards that reflect your brand. Unlimited customization options and professional templates.",
    image: "/assets/custom.jpg",
    keywords: "custom business cards, card design, personalized cards, branding, custom templates"
  });

  const materials = [
    { 
      id: 'metal', 
      name: 'Premium Metal', 
      icon: 'shield-alt', 
      color: 'from-slate-600 to-zinc-800', 
      price: 29.99,
      description: 'Luxury stainless steel with engraving',
      features: ['Waterproof', 'Scratch-resistant', '2mm thickness', '5-year warranty'],
      cardGradient: 'from-slate-700 via-zinc-700 to-slate-800',
      cardStyles: {
        background: 'linear-gradient(145deg, #334155 0%, #3f3f46 50%, #1e293b 100%)',
        boxShadow: '0 20px 40px rgba(0,0,0,0.4)'
      }
    },
    { 
      id: 'plastic', 
      name: 'Premium Plastic', 
      icon: 'credit-card', 
      color: 'from-blue-500 to-purple-600', 
      price: 11.99,
      description: 'Durable PVC with full-color printing',
      features: ['Full-color design', 'Matte/Glossy finish', 'Standard thickness', '2-year warranty'],
      cardGradient: 'from-blue-600 via-purple-600 to-pink-600',
      cardStyles: {
        background: 'linear-gradient(135deg, #3B82F6 0%, #9333EA 50%, #EC4899 100%)',
        boxShadow: '0 10px 30px rgba(147, 51, 234, 0.3)'
      }
    },
    { 
      id: 'bamboo', 
      name: 'Eco Bamboo', 
      icon: 'leaf', 
      color: 'from-green-500 to-emerald-600', 
      price: 24.99,
      description: 'Sustainable bamboo with laser engraving',
      features: ['100% Eco-friendly', 'Natural texture', 'Laser engraving', '3-year warranty'],
      cardGradient: 'from-emerald-600 via-green-600 to-teal-600',
      cardStyles: {
        background: 'linear-gradient(135deg, #059669 0%, #16A34A 50%, #14B8A6 100%)',
        boxShadow: '0 10px 30px rgba(5, 150, 105, 0.3)'
      }
    },
    { 
      id: 'wood', 
      name: 'Premium Wood', 
      icon: 'tree', 
      color: 'from-amber-600 to-amber-800', 
      price: 25.99,
      description: 'Genuine wood with custom engraving',
      features: ['Real wood grain', 'Multiple wood types', 'Premium finish', '3-year warranty'],
      cardGradient: 'from-amber-700 via-orange-700 to-amber-800',
      cardStyles: {
        background: 'linear-gradient(135deg, #D97706 0%, #EA580C 50%, #DC2626 100%)',
        boxShadow: '0 10px 30px rgba(217, 119, 6, 0.3)'
      }
    },
    { 
      id: 'bracelet', 
      name: 'NFC Bracelet', 
      icon: 'circle', 
      color: 'from-purple-500 to-pink-600', 
      price: 9.99,
      description: 'Waterproof silicone NFC wristband',
      features: ['Waterproof', 'Adjustable size', 'Multiple colors', '1-year warranty'],
      cardGradient: 'from-purple-600 via-pink-600 to-purple-600',
      cardStyles: {
        background: 'linear-gradient(135deg, #9333EA 0%, #EC4899 50%, #9333EA 100%)',
        boxShadow: '0 10px 30px rgba(236, 72, 153, 0.3)'
      }
    }
  ];

  useEffect(() => {
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": "Custom NFC Business Cards",
        "description": "Design unique custom NFC business cards",
        "brand": {
          "@type": "Brand",
          "name": "AM CARDMATE"
        },
        "offers": materials.map(material => ({
          "@type": "Offer",
          "name": `Custom ${material.name}`,
          "price": material.price,
          "priceCurrency": "GBP",
          "availability": "https://schema.org/InStock",
          "priceValidUntil": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
        })),
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.9",
          "reviewCount": "250"
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Custom Card Design Service",
        "provider": {
          "@type": "Organization",
          "name": "AM CARDMATE"
        },
        "serviceType": "Custom Business Card Design",
        "areaServed": "Worldwide"
      }
    ];
    
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
  }, []);

  const [selectedMaterial, setSelectedMaterial] = useState('metal');
  const [selectedElements, setSelectedElements] = useState([]);
  const [customQuantity, setCustomQuantity] = useState(100);
  const [activeTab, setActiveTab] = useState('design');
  const [selectedSample, setSelectedSample] = useState(0);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  
  const { addToCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const scrollToSamples = () => {
    const samplesSection = document.getElementById('sample-designs');
    if (samplesSection) {
      samplesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  const calculateCustomPrice = () => {
    let basePrice = selectedMaterial === 'metal' ? 29.99 :
                   selectedMaterial === 'plastic' ? 11.99 :
                   selectedMaterial === 'bamboo' ? 24.99 : 
                   selectedMaterial === 'wood' ? 25.99 : 9.99;
    
    const elementPrices = {
      'logo': 0.00,
      'qr': 0.99,
      'custom_color': 0.00
    };
    
    let totalElementPrice = selectedElements.reduce((sum, elementId) => {
      return sum + (elementPrices[elementId] || 0);
    }, 0);
    
    let unitPrice = basePrice + totalElementPrice;
    
    let discount = 0;
    if (customQuantity >= 1000) discount = 0.30;
    else if (customQuantity >= 500) discount = 0.25;
    else if (customQuantity >= 250) discount = 0.15;
    else if (customQuantity >= 100) discount = 0.10;
    else if (customQuantity >= 50) discount = 0.05;
    
    return {
      unitPrice: unitPrice.toFixed(2),
      discount: discount,
      discountedPrice: (unitPrice * (1 - discount)).toFixed(2),
      totalPrice: (unitPrice * (1 - discount) * customQuantity).toFixed(2),
      savedAmount: (unitPrice * discount * customQuantity).toFixed(2)
    };
  };
  
  const customPricing = calculateCustomPrice();

  const handleAddToCart = async () => {
    if (!user) {
      toast.error('Please login to add items to cart');
      navigate('/login');
      return;
    }

    const selectedMat = materials.find(m => m.id === selectedMaterial);
    const selectedCustomizations = selectedElements.map(el => 
      customizationOptions.find(opt => opt.id === el).name
    );
    
    const cartItem = {
      id: `custom-${selectedMaterial}-${Date.now()}`,
      name: `Custom ${selectedMat.name}`,
      description: `${customQuantity} cards with ${selectedCustomizations.length > 0 ? selectedCustomizations.join(', ') : 'no customizations'}`,
      price: parseFloat(customPricing.discountedPrice),
      quantity: customQuantity,
      image: null, 
      type: 'custom-card',
      material: selectedMat.name,
      customizations: selectedCustomizations
    };

    try {
      await addToCart(cartItem);
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add item to cart');
    }
  };

  const handleBuyNow = async () => {
    await handleAddToCart();
    navigate('/cart');
  };
  
  const downloadQuote = () => {
    const quoteData = {
      date: new Date().toLocaleDateString(),
      material: materials.find(m => m.id === selectedMaterial).name,
      quantity: customQuantity,
      customizations: selectedElements.map(el => customizationOptions.find(opt => opt.id === el).name),
      unitPrice: customPricing.discountedPrice,
      totalPrice: customPricing.totalPrice,
      discount: customPricing.discount,
      savedAmount: customPricing.savedAmount
    };
    
    const quoteText = `
AM CARDMATE - Custom Card Quote
===============================
Date: ${quoteData.date}

Order Details:
--------------
Material: ${quoteData.material}
Quantity: ${quoteData.quantity} cards
Customizations: ${quoteData.customizations.length > 0 ? quoteData.customizations.join(', ') : 'None'}

Pricing:
--------
Unit Price: £${quoteData.unitPrice}
Volume Discount: ${(quoteData.discount * 100).toFixed(0)}%
You Save: £${quoteData.savedAmount}
Total Price: £${quoteData.totalPrice}

Valid for 30 days from quote date.
Contact: sales@amcardmate.com
    `;
    
    const blob = new Blob([quoteText], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `AM_CARDMATE_Quote_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    setShowQuoteModal(false);
  };

  const customizationOptions = [
    { id: 'logo', name: 'Company Logo', price: 0.00, icon: 'building', description: 'High-quality logo engraving' },
    { id: 'qr', name: 'Custom QR Code', price: 0.99, icon: 'qrcode', description: 'Dynamic QR with tracking' },
    { id: 'custom_color', name: 'Brand Colors', price: 0.00, icon: 'palette', description: 'Match your brand colors' }
  ];

  const sampleDesigns = [
    { 
      id: 0, 
      name: 'Ocean Depths', 
      style: 'Deep navy blues with oceanic elegance', 
      gradient: 'from-sky-400 via-blue-600 to-indigo-900',
      accent: 'from-sky-300 to-indigo-700'
    },
    { 
      id: 1, 
      name: 'Royal Gold', 
      style: 'Luxurious gold with premium navy accents', 
      gradient: 'from-amber-400 via-yellow-500 to-amber-600',
      accent: 'from-amber-300 to-yellow-600'
    },
    { 
      id: 2, 
      name: 'Executive Navy', 
      style: 'Professional navy with silver highlights', 
      gradient: 'from-slate-700 via-indigo-800 to-slate-900',
      accent: 'from-slate-600 to-indigo-700'
    },
    { 
      id: 3, 
      name: 'Champagne Dreams', 
      style: 'Elegant champagne gold with rose accents', 
      gradient: 'from-amber-200 via-rose-300 to-amber-400',
      accent: 'from-rose-200 to-amber-300'
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="pt-20 md:pt-24 lg:pt-32 pb-16 sm:pb-20 md:pb-24 bg-gradient-to-b from-white via-blue-50/30 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] sm:w-[600px] md:w-[800px] h-[400px] sm:h-[600px] md:h-[800px] bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full filter blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[300px] sm:w-[500px] md:w-[600px] h-[300px] sm:h-[500px] md:h-[600px] bg-gradient-to-tr from-purple-200/20 to-pink-200/20 rounded-full filter blur-3xl" />
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center max-w-5xl mx-auto">
            <div className="badge badge-blue inline-flex mb-4 sm:mb-6">
              <FontAwesomeIcon icon="palette" />
              <span>Custom Design Studio</span>
            </div>
            
            <h1 className="text-h1 mb-4 sm:mb-6">
              Create Your Perfect <span className="text-gradient-blue-pink">Custom Cards</span>
            </h1>
            
            <p className="text-body-lg text-gray max-w-3xl mx-auto mb-8 sm:mb-10">
              Design premium NFC business cards that match your brand perfectly. 
              From materials to finishes, create cards that make lasting impressions.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <a 
                href="#design-studio" 
                className="btn btn-primary group w-full sm:w-auto"
              >
                <FontAwesomeIcon icon="magic" />
                <span>Start Designing</span>
                <FontAwesomeIcon 
                  icon="arrow-right" 
                  className="ml-1 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" 
                />
              </a>
              
              <button 
                className="btn btn-outline group w-full sm:w-auto"
                onClick={scrollToSamples}
              >
                <FontAwesomeIcon icon="images" />
                <span>View Samples</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Design Studio Section */}
      <section id="design-studio" className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-7xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-8 sm:mb-12">
              <div className="badge badge-purple inline-flex mb-4 sm:mb-6">
                <FontAwesomeIcon icon="magic" />
                <span>Design Studio</span>
              </div>
              
              <h2 className="text-h2 mb-4 sm:mb-6">
                Build Your <span className="text-gradient-blue-pink">Dream Card</span>
              </h2>
              
              <p className="text-body-lg text-gray max-w-3xl mx-auto">
                Choose materials, add customizations, and see instant pricing with volume discounts
              </p>
            </div>

            {/* Design Studio Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
              {/* Left Column - Material Selection & Preview */}
              <div>
                {/* Material Selection */}
                <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl p-4 sm:p-6 md:p-8 mb-4 sm:mb-6">
                  <h3 className="text-h4 text-dark mb-4 sm:mb-6">Select Your Material</h3>
                  
                  <div className="space-y-3 sm:space-y-4">
                    {materials.map((material, index) => (
                      <div
                        key={material.id}
                        className={`relative p-4 sm:p-6 rounded-xl sm:rounded-2xl cursor-pointer transition-all ${
                          selectedMaterial === material.id 
                            ? 'bg-gradient-to-r from-blue-50 to-purple-50 ring-2 ring-blue-400' 
                            : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                        onClick={() => setSelectedMaterial(material.id)}
                      >
                        <div className="flex items-center gap-3 sm:gap-4">
                          <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br ${material.color} rounded-xl sm:rounded-2xl flex items-center justify-center text-white shadow-lg`}>
                            <FontAwesomeIcon icon={material.icon} className="text-lg sm:text-2xl" />
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1 sm:mb-2">
                              <h4 className="text-card-title text-dark">{material.name}</h4>
                              <span className="text-h4 text-gradient-blue-pink">£{material.price}</span>
                            </div>
                            <p className="text-card-body">{material.description}</p>
                            
                            <div className="flex flex-wrap gap-1 sm:gap-2 mt-2 sm:mt-3">
                              {material.features.slice(0, 2).map((feature, idx) => (
                                <span key={idx} className="text-caption bg-white px-2 sm:px-3 py-1 rounded-full">
                                  {feature}
                                </span>
                              ))}
                            </div>
                          </div>
                          
                          {selectedMaterial === material.id && (
                            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-500 rounded-full flex items-center justify-center">
                              <FontAwesomeIcon icon="check" className="text-white text-xs sm:text-sm" />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Live Preview */}
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-xl">
                  <h3 className="text-h5 text-dark mb-4 sm:mb-6">Live Preview</h3>
                  
                  <div className="relative">
                    {selectedMaterial === 'bracelet' ? (
                      /* Bracelet Preview */
                      <div className="relative w-full max-w-md mx-auto h-48 sm:h-56 md:h-64 flex items-center justify-center">
                        <div className="relative">
                          <div className="relative">
                            <div 
                              className="w-56 h-32 rounded-full border-8 relative overflow-hidden shadow-2xl"
                              style={{
                                borderColor: '#9333EA',
                                background: 'linear-gradient(135deg, #9333EA 0%, #EC4899 50%, #9333EA 100%)',
                                transform: 'perspective(100px) rotateX(15deg)'
                              }}
                            >
                              <div className="absolute inset-2 rounded-full bg-gradient-to-br from-black/10 to-transparent" />
                              <div className="absolute top-2 left-8 w-24 h-4 bg-white/30 rounded-full blur-sm" />
                            </div>
                            
                            <div 
                              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-10 bg-white/20 rounded-lg backdrop-blur-sm flex items-center justify-center"
                              style={{ transform: 'translate(-50%, -50%) perspective(100px) rotateX(15deg)' }}
                            >
                              <div className="text-center">
                                {selectedElements.includes('logo') && (
                                  <div className="w-6 h-6 bg-white/30 rounded-md flex items-center justify-center backdrop-blur-sm mb-1 mx-auto">
                                    <FontAwesomeIcon icon="building" className="text-xs text-white" />
                                  </div>
                                )}
                                <FontAwesomeIcon icon="wifi" className="text-white text-lg" />
                                <div className="text-caption text-white/90 font-medium mt-1">NFC</div>
                              </div>
                            </div>
                            
                            <div className="absolute -right-2 top-1/2 transform -translate-y-1/2 w-6 h-8 bg-gradient-to-br from-gray-400 to-gray-600 rounded-sm shadow-lg" />
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Card Preview */
                      <div 
                        className="relative w-full max-w-md mx-auto h-48 sm:h-56 md:h-64 rounded-xl sm:rounded-2xl shadow-2xl overflow-hidden"
                        style={materials.find(m => m.id === selectedMaterial).cardStyles}
                      >
                        <div className="absolute inset-0 p-4 sm:p-6 flex flex-col justify-between text-white">
                          <div>
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="text-h5 font-bold">Your Name</h4>
                                <p className="text-body-sm opacity-90">Your Title</p>
                              </div>
                              {selectedElements.includes('logo') && (
                                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white/20 rounded-lg sm:rounded-xl flex items-center justify-center backdrop-blur-sm">
                                  <FontAwesomeIcon icon="building" className="text-xl sm:text-2xl" />
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex justify-between items-end">
                            {selectedElements.includes('qr') && (
                              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white/10 rounded-lg sm:rounded-xl flex items-center justify-center">
                                <FontAwesomeIcon icon="qrcode" className="text-2xl sm:text-3xl" />
                              </div>
                            )}
                            <FontAwesomeIcon icon="wifi" className="text-xl sm:text-2xl" />
                          </div>
                        </div>
                        
                        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent" />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column - Customization & Pricing */}
              <div>
                {/* Customization Options */}
                <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl p-4 sm:p-6 md:p-8 mb-4 sm:mb-6">
                  <h3 className="text-h4 text-dark mb-4 sm:mb-6">Customize Your Design</h3>
                  
                  <div className="space-y-3 sm:space-y-4">
                    {customizationOptions.map((option, index) => (
                      <label 
                        key={option.id}
                        className={`flex items-center justify-between p-3 sm:p-4 rounded-xl cursor-pointer transition-all ${
                          selectedElements.includes(option.id)
                            ? 'bg-gradient-to-r from-blue-50 to-purple-50 ring-2 ring-blue-400'
                            : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center gap-3 sm:gap-4">
                          <input 
                            type="checkbox"
                            checked={selectedElements.includes(option.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedElements([...selectedElements, option.id]);
                              } else {
                                setSelectedElements(selectedElements.filter(el => el !== option.id));
                              }
                            }}
                            className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-400 to-purple-500 rounded-lg sm:rounded-xl flex items-center justify-center text-white">
                              <FontAwesomeIcon icon={option.icon} className="text-sm sm:text-base" />
                            </div>
                            <div>
                              <h4 className="text-body font-semibold text-dark">{option.name}</h4>
                              <p className="text-body-sm text-gray">{option.description}</p>
                            </div>
                          </div>
                        </div>
                        <span className="text-body font-bold text-gray-600">
                          {option.price === 0 ? (
                            <span className="text-green-600 font-bold">FREE</span>
                          ) : (
                            `+£${option.price}`
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                  
                  {/* Quantity Selector */}
                  <div className="mt-6 sm:mt-8">
                    <div className="flex justify-between items-center mb-3 sm:mb-4">
                      <h4 className="text-body font-semibold text-dark">Order Quantity</h4>
                      <span className="text-h4 text-gradient-blue-pink">{customQuantity.toLocaleString()}</span>
                    </div>
                    
                    <input 
                      type="range" 
                      min="10" 
                      max="5000" 
                      step="10"
                      value={customQuantity}
                      onChange={(e) => setCustomQuantity(parseInt(e.target.value))}
                      className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      style={{
                        background: `linear-gradient(to right, #3B82F6 0%, #9333EA ${(customQuantity - 10) / 4990 * 100}%, #E5E7EB ${(customQuantity - 10) / 4990 * 100}%, #E5E7EB 100%)`
                      }}
                    />
                    
                    <div className="flex gap-1 sm:gap-2 mt-3 sm:mt-4">
                      {[50, 100, 250, 500, 1000].map((qty) => (
                        <button
                          key={qty}
                          className={`flex-1 py-1.5 sm:py-2 rounded-lg text-btn-sm transition-all ${
                            customQuantity === qty 
                              ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white' 
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          onClick={() => setCustomQuantity(qty)}
                        >
                          {qty}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Pricing Summary */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-xl">
                  <h3 className="text-h4 text-dark mb-4 sm:mb-6">Your Investment</h3>
                  
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex justify-between items-center pb-3 sm:pb-4 border-b border-green-200">
                      <span className="text-body text-gray-600">Unit price</span>
                      <span className="text-h5">£{customPricing.unitPrice}</span>
                    </div>
                    
                    {customPricing.discount > 0 && (
                      <>
                        <div className="flex justify-between items-center text-green-600">
                          <span className="flex items-center gap-2 text-body">
                            <FontAwesomeIcon icon="tag" />
                            Volume discount ({(customPricing.discount * 100).toFixed(0)}% OFF)
                          </span>
                          <span className="text-body font-bold">-£{customPricing.savedAmount}</span>
                        </div>
                        
                        <div className="flex justify-between items-center pb-3 sm:pb-4 border-b border-green-200">
                          <span className="text-body text-gray-600">Discounted unit price</span>
                          <span className="text-h5 text-green-600">£{customPricing.discountedPrice}</span>
                        </div>
                      </>
                    )}
                    
                    <div className="pt-3 sm:pt-4">
                      <div className="flex justify-between items-center mb-4 sm:mb-6">
                        <span className="text-h4 text-dark">Total</span>
                        <span className="text-h3 text-gradient-blue-pink">
                          £{customPricing.totalPrice}
                        </span>
                      </div>
                      
                      {customPricing.discount > 0 && (
                        <div className="bg-green-100 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                          <p className="text-body-sm text-green-700 font-medium flex items-center gap-2">
                            <FontAwesomeIcon icon="check-circle" />
                            You save £{customPricing.savedAmount} with bulk pricing!
                          </p>
                        </div>
                      )}
                      
                      <div className="space-y-2 sm:space-y-3">
                        <button 
                          onClick={handleAddToCart}
                          className="w-full btn btn-primary"
                        >
                          <FontAwesomeIcon icon="shopping-cart" />
                          <span>Add to Cart</span>
                        </button>
                        
                        <button 
                          onClick={handleBuyNow}
                          className="w-full btn btn-outline"
                        >
                          <FontAwesomeIcon icon="bolt" />
                          <span>Buy Now</span>
                        </button>
                        
                        <button 
                          className="w-full btn btn-outline"
                          onClick={() => setShowQuoteModal(true)}
                        >
                          <FontAwesomeIcon icon="file-download" />
                          <span>Download Quote</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sample Designs Section */}
      <section id="sample-designs" className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12">
            <div className="badge badge-pink inline-flex mb-4 sm:mb-6">
              <FontAwesomeIcon icon="sparkles" />
              <span>Design Inspiration</span>
            </div>
            
            <h2 className="text-h2 mb-4 sm:mb-6">
              Get Inspired by Our <span className="text-gradient-blue-pink">Templates</span>
            </h2>
            
            <p className="text-body-lg text-gray">
              Choose from our professionally designed templates or create your own unique design
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              {sampleDesigns.map((sample, index) => (
                <div
                  key={sample.id}
                  className="group"
                >
                  <div className="relative bg-white rounded-2xl sm:rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-all">
                    {/* Card Preview */}
                    <div className={`h-48 sm:h-56 md:h-64 bg-gradient-to-br ${sample.gradient} p-6 sm:p-8 relative overflow-hidden`}>
                      <div className="relative z-10 h-full flex flex-col justify-between text-white">
                        <div>
                          <h3 className="text-h4 mb-1">Professional Name</h3>
                          <p className="text-body-sm opacity-90">Job Title • Company</p>
                        </div>
                        
                        <div className="flex justify-between items-end">
                          <div className="flex gap-2 sm:gap-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                              <FontAwesomeIcon icon="qrcode" className="text-sm sm:text-base" />
                            </div>
                          </div>
                          <FontAwesomeIcon icon="wifi" className="text-xl sm:text-2xl opacity-80" />
                        </div>
                      </div>
                      
                      <div className={`absolute inset-0 bg-gradient-to-r ${sample.accent} opacity-0 group-hover:opacity-20 transition-opacity`} />
                    </div>
                    
                    {/* Card Info */}
                    <div className="p-4 sm:p-6">
                      <h4 className="text-card-title text-dark mb-1 sm:mb-2">{sample.name}</h4>
                      <p className="text-card-body mb-3 sm:mb-4">{sample.style}</p>
                      
                      <button 
                        className="btn btn-primary w-full"
                        onClick={() => navigate('/contact')}
                      >
                        <FontAwesomeIcon icon="comments" />
                        Contact Sales Team
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Custom Design CTA */}
            <div className="mt-8 sm:mt-12 bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 text-center">
              <h3 className="text-h4 text-dark mb-3 sm:mb-4">Need Something Unique?</h3>
              <p className="text-body text-gray mb-4 sm:mb-6 max-w-2xl mx-auto">
                Our design team can create a completely custom design that perfectly matches your brand identity
              </p>
              <button 
                className="btn btn-primary"
                onClick={() => navigate('/contact')}
              >
                <FontAwesomeIcon icon="paint-brush" />
                <span>Request Custom Design</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Production Journey Section */}
      <section className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-white via-blue-50/20 to-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12">
            <div className="badge badge-green inline-flex mb-4 sm:mb-6">
              <FontAwesomeIcon icon="route" />
              <span>Production Journey</span>
            </div>
            
            <h2 className="text-h2 mb-4 sm:mb-6">
              From Concept to <span className="text-gradient-blue-pink">Delivery</span>
            </h2>
            
            <p className="text-body-lg text-gray">
              Your custom cards delivered in just 10-14 business days
            </p>
          </div>
          
          {/* Timeline */}
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {[
                { 
                  icon: 'pencil-ruler', 
                  title: 'Design Approval', 
                  time: '1-2 days', 
                  description: 'We create mockups for your review',
                  color: 'from-blue-400 to-blue-600'
                },
                { 
                  icon: 'industry', 
                  title: 'Production', 
                  time: '5-7 days', 
                  description: 'Precision manufacturing begins',
                  color: 'from-purple-400 to-purple-600'
                },
                { 
                  icon: 'check-double', 
                  title: 'Quality Check', 
                  time: '1 day', 
                  description: 'Every card inspected for perfection',
                  color: 'from-green-400 to-green-600'
                },
                { 
                  icon: 'shipping-fast', 
                  title: 'Express Shipping', 
                  time: '2-3 days', 
                  description: 'Delivered to your door worldwide',
                  color: 'from-orange-400 to-red-600'
                }
              ].map((step, index) => (
                <div key={index} className="relative">
                  {/* Connector Line */}
                  {index < 3 && (
                    <div className="hidden lg:block absolute top-20 left-1/2 w-full h-0.5 bg-gradient-to-r from-gray-300 to-gray-300 -z-10" />
                  )}
                  
                  <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all">
                    {/* Step Number */}
                    <div className="absolute -top-3 -right-3 w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-body-sm">
                      {index + 1}
                    </div>
                    
                    {/* Icon */}
                    <div className={`w-20 h-20 bg-gradient-to-br ${step.color} rounded-2xl flex items-center justify-center text-white mx-auto mb-4`}>
                      <FontAwesomeIcon icon={step.icon} className="text-3xl" />
                    </div>
                    
                    <h3 className="text-card-title text-dark mb-2">{step.title}</h3>
                    <p className="text-body-sm text-gray mb-3">{step.description}</p>
                    
                    <div className="inline-flex items-center gap-1 text-body-sm font-medium text-green-600 bg-green-50 px-3 py-1 rounded-full">
                      <FontAwesomeIcon icon="clock" className="text-xs" />
                      <span>{step.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Progress Bar */}
            <div className="mt-12 bg-gray-100 rounded-full h-3 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-400 via-purple-500 to-green-500 w-full rounded-full shadow-inner">
                <div className="h-full bg-white/30 animate-pulse" />
              </div>
            </div>
            
            <p className="text-center mt-4 text-meta">
              <FontAwesomeIcon icon="shield-alt" className="text-green-500 mr-2" />
              100% satisfaction guarantee on every order
            </p>
          </div>
        </div>
      </section>

      {/* Custom Design Help CTA */}
      <section className="py-16 bg-gradient-to-r from-[#4DA2FF] to-[#6FB1FF] relative overflow-hidden">
        <div className="absolute inset-0 bg-dots-pattern opacity-10"></div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full filter blur-3xl" />
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-[#DE6161]/20 rounded-full filter blur-3xl" />
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-h2 mb-6">
              <span className="text-white">Need Help With Your </span>
              <span className="bg-gradient-to-r from-white via-yellow-100 to-white bg-clip-text text-transparent">Custom Design?</span>
            </h2>
            
            <p className="text-body-lg text-white/90 mb-8 max-w-2xl mx-auto">
              Our design experts are here to help you create the perfect cards for your brand. 
              Get personalized assistance and professional guidance.
            </p>
            
            <div className="flex justify-center">
              <a 
                href="/contact" 
                className="btn inline-flex items-center gap-3 px-8 py-4 text-btn-lg bg-white text-[#4DA2FF] hover:bg-gray-100 transition-all shadow-lg"
              >
                <FontAwesomeIcon icon="comments" />
                <span>Contact Our Team</span>
              </a>
            </div>
            
            <div className="mt-6 text-body-sm text-white/80">
              <FontAwesomeIcon icon="clock" className="mr-2" />
              Available Monday-Friday, 9AM-6PM GMT
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-white to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12">
            <div className="badge badge-blue inline-flex mb-4 sm:mb-6">
              <FontAwesomeIcon icon="star" />
              <span>Why Go Custom</span>
            </div>
            
            <h2 className="text-h2 mb-4 sm:mb-6">
              The Smart Choice for <span className="text-gradient-blue-pink">Growing Brands</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-6xl mx-auto">
            {[
              { 
                icon: 'fingerprint', 
                title: 'Unique Identity', 
                description: 'Stand out with cards that perfectly match your brand vision',
                gradient: 'from-blue-400 to-blue-600'
              },
              { 
                icon: 'award', 
                title: 'Premium Quality', 
                description: 'Superior materials and craftsmanship in every card',
                gradient: 'from-purple-400 to-purple-600'
              },
              { 
                icon: 'users', 
                title: 'Team Unity', 
                description: 'Consistent professional look across your organization',
                gradient: 'from-green-400 to-green-600'
              },
              { 
                icon: 'chart-line', 
                title: 'Better ROI', 
                description: 'Track engagement and see higher interaction rates with memorable designs',
                gradient: 'from-orange-400 to-red-600'
              },
              { 
                icon: 'shield-alt', 
                title: 'Quality Promise', 
                description: '100% satisfaction guarantee on every order',
                gradient: 'from-pink-400 to-pink-600'
              },
              { 
                icon: 'headset', 
                title: 'VIP Support', 
                description: 'Dedicated account manager for your success',
                gradient: 'from-cyan-400 to-blue-600'
              }
            ].map((benefit, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all"
              >
                <div className={`w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br ${benefit.gradient} rounded-xl flex items-center justify-center text-white mb-3 sm:mb-4 shadow-lg`}>
                  <FontAwesomeIcon icon={benefit.icon} className="text-xl sm:text-2xl" />
                </div>
                <h3 className="text-card-title text-dark mb-2">{benefit.title}</h3>
                <p className="text-card-body">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 sm:py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12">
            <div className="badge badge-green inline-flex mb-4 sm:mb-6">
              <FontAwesomeIcon icon="comments" />
              <span>Success Stories</span>
            </div>
            
            <h2 className="text-h2 mb-4 sm:mb-6">
              Trusted by <span className="text-gradient-blue-pink">Leading Brands</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-5xl mx-auto">
            {[
              {
                quote: "The custom metal cards elevated our brand presence. Clients are always impressed!",
                author: "James Wilson",
                role: "CEO, TechVentures",
                rating: 5
              },
              {
                quote: "Our team loves the eco bamboo cards. Perfect alignment with our sustainability values.",
                author: "Sarah Green",
                role: "Director, EcoSolutions",
                rating: 5
              },
              {
                quote: "The design team created exactly what we envisioned. Outstanding quality and service!",
                author: "Michael Chen",
                role: "Founder, Creative Agency",
                rating: 5
              }
            ].map((testimonial, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg"
              >
                <div className="flex gap-1 mb-3 sm:mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FontAwesomeIcon key={i} icon="star" className="text-yellow-500 text-sm" />
                  ))}
                </div>
                <p className="text-body text-gray mb-3 sm:mb-4 italic">"{testimonial.quote}"</p>
                <div>
                  <p className="text-body font-bold text-dark">{testimonial.author}</p>
                  <p className="text-body-sm text-gray">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ />

      {/* CTA Section */}
      <CTA 
        title="Ready to Create Your Custom Cards?"
        description="Get 20% off your first custom order. Design premium NFC cards that make lasting impressions."
        primaryButtonText="Start Your Design"
        primaryButtonIcon="magic"
        primaryButtonHref="#design-studio"
        secondaryButtonText="Talk to Sales"
        secondaryButtonIcon="comments"
        background="gradient"
      />

      {/* Quote Modal */}
      {showQuoteModal && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowQuoteModal(false)}
        >
          <div 
            className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-blue-400 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <FontAwesomeIcon icon="file-download" className="text-white text-2xl sm:text-3xl" />
              </div>
              
              <h3 className="text-h4 text-dark mb-3 sm:mb-4">Download Your Quote</h3>
              <p className="text-body text-gray mb-4 sm:mb-6">
                Get a detailed quote for your custom NFC cards order including all specifications and pricing.
              </p>
              
              <div className="space-y-2 sm:space-y-3">
                <button 
                  className="w-full btn btn-primary"
                  onClick={downloadQuote}
                >
                  <FontAwesomeIcon icon="download" />
                  <span>Download Quote</span>
                </button>
                
                <button 
                  className="w-full btn btn-outline"
                  onClick={() => setShowQuoteModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CustomCards;